<template>
  <el-dialog
    v-model="dialogVisible"
    title="API 配置管理"
    width="50%"
    :close-on-click-modal="false"
  >
    <div class="api-config-container">
      <div class="api-list">
        <div class="list-header">
          <h3>API 配置列表</h3>
          <el-button type="warning" size="small" @click="handleClearCache">
            清理缓存
          </el-button>
        </div>
        <el-table :data="apiConfigs" style="width: 100%">
          <el-table-column prop="name" label="名称" width="120">
            <template #default="{ row }">
              <span>{{ row.name === 'kimi' ? 'Kimi API' : row.name === 'deepseek' ? 'Deepseek API' : row.name }}</span>
            </template>
          </el-table-column>
          
          <el-table-column prop="model" label="模型" width="150">
            <template #default="{ row }">
              <span>{{ row.model }}</span>
            </template>
          </el-table-column>
          
          <el-table-column prop="maxTokens" label="最大Token" width="100">
            <template #default="{ row }">
              <span>{{ row.max_tokens }}</span>
            </template>
          </el-table-column>
          
          <el-table-column prop="status" label="状态" width="100">
            <template #default="{ row }">
              <el-tag :type="row.status === 'active' ? 'success' : 'info'">
                {{ row.status === 'active' ? '启用' : '禁用' }}
              </el-tag>
            </template>
          </el-table-column>
          
          <el-table-column label="操作" width="200">
            <template #default="{ row }">
              <el-button
                size="small"
                :type="row.status === 'active' ? 'warning' : 'success'"
                @click="handleToggleStatus(row)"
              >
                {{ row.status === 'active' ? '禁用' : '启用' }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="add-api-form">
        <el-form ref="formRef" :model="form" :rules="rules" label-width="80px">
          <el-form-item label="名称" prop="name">
            <el-select v-model="form.name" placeholder="请选择API类型" @change="handleApiTypeChange">
              <el-option label="Kimi API" value="kimi" />
              <el-option label="Deepseek API" value="deepseek" />
            </el-select>
          </el-form-item>
          <el-form-item label="API Key" prop="apiKey">
            <el-input v-model="form.apiKey" :placeholder="form.name === 'kimi' ? '请输入 Kimi API Key' : '请输入 Deepseek API Key'" />
          </el-form-item>
          <el-form-item label="最大Token" prop="maxTokens">
            <el-input-number v-model="form.max_tokens" :min="1000" :max="8000" :step="100" />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="handleAdd" :loading="loading">
              添加
            </el-button>
          </el-form-item>
        </el-form>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useStore } from 'vuex'
import request from '@/utils/request'

export default {
  name: 'ApiConfig',
  props: {
    modelValue: {
      type: Boolean,
      required: true
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const store = useStore()
    const formRef = ref(null)
    const loading = ref(false)
    const apiConfigs = ref([])

    const form = ref({
      name: '',
      apiKey: '',
      max_tokens: 4096
    })

    const rules = {
      name: [
        { required: true, message: '请选择API类型', trigger: 'change' }
      ],
      apiKey: [
        { required: true, message: '请输入 API Key', trigger: 'blur' },
        { min: 32, message: 'API Key 长度不正确', trigger: 'blur' }
      ],
      max_tokens: [
        { required: true, message: '请输入最大Token', trigger: 'blur' },
        { type: 'number', min: 1000, max: 8000, message: '最大Token应在1000到8000之间', trigger: 'blur' }
      ]
    }

    const dialogVisible = computed({
      get: () => props.modelValue,
      set: (value) => emit('update:modelValue', value)
    })

    const maskApiKey = (key) => {
      if (!key) return ''
      return `${key.substring(0, 8)}...${key.substring(key.length - 8)}`
    }

    const loadApiConfigs = async () => {
      try {
        const response = await request.get('/api/admin/api-configs')
        apiConfigs.value = response
      } catch (error) {
        ElMessage.error('加载 API 配置失败')
      }
    }

    const handleApiTypeChange = (value) => {
      if (value === 'kimi') {
        form.value.max_tokens = 4096
      } else if (value === 'deepseek') {
        form.value.max_tokens = 5000
      }
    }

    const handleAdd = async () => {
      if (!formRef.value) return
      
      try {
        await formRef.value.validate()
        loading.value = true
        
        // 检查是否已有同名配置
        const existingConfig = apiConfigs.value.find(config => config.name === form.value.name)
        if (existingConfig) {
          ElMessage.warning('该API配置名称已存在')
          return
        }

        // 检查是否有其他配置已启用
        const hasActiveConfig = apiConfigs.value.some(config => config.active)
        if (hasActiveConfig) {
          await ElMessageBox.confirm(
            '只能启用一个API配置，添加并启用此配置将会禁用其他配置，是否继续？',
            '提示',
            {
              confirmButtonText: '确定',
              cancelButtonText: '取消',
              type: 'warning'
            }
          )
        }
        
        const model = form.value.name === 'kimi' ? 'moonshot-v1-32k' : 'deepseek-reasoner'
        
        await request.post('/api/admin/api-configs', {
          name: form.value.name,
          apiKey: form.value.apiKey,
          model: model,
          max_tokens: form.value.max_tokens
        })
        
        ElMessage.success('添加成功')
        form.value.name = ''
        form.value.apiKey = ''
        form.value.max_tokens = 4096
        await loadApiConfigs()
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error(error.response?.data || '添加失败')
        }
      } finally {
        loading.value = false
      }
    }

    const handleToggleStatus = async (row) => {
      try {
        // 如果要启用配置，先检查是否有其他配置已启用
        if (!row.active && apiConfigs.value.some(config => config.active)) {
          // 提示用户只能启用一个配置
          await ElMessageBox.confirm(
            '只能启用一个API配置，启用此配置将会禁用其他配置，是否继续？',
            '提示',
            {
              confirmButtonText: '确定',
              cancelButtonText: '取消',
              type: 'warning'
            }
          )
        }

        await request.put(`/api/admin/api-configs/${row.name}/toggle`)
        ElMessage.success('状态更新成功')
        await loadApiConfigs()
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error(error.message || '操作失败')
        }
      }
    }

    const handleClearCache = async () => {
      try {
        await ElMessageBox.confirm(
          '确定要清理API配置缓存吗？这可能会影响系统的正常使用。',
          '警告',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        await request.post('/api/admin/api-configs/clear-cache')
        ElMessage.success('缓存清理成功')
        await loadApiConfigs()
      } catch (error) {
        if (error !== 'cancel')
          ElMessage.error(error.message || '清理缓存失败')
      }
    }

    onMounted(() => {
      if (store.state.isAdmin) {
        loadApiConfigs()
      }
    })

    return {
      formRef,
      form,
      rules,
      loading,
      apiConfigs,
      dialogVisible,
      maskApiKey,
      handleAdd,
      handleApiTypeChange,
      handleToggleStatus,
      handleClearCache
    }
  }
}
</script>

<style scoped>
.api-config-container {
  padding: 20px;
}

.api-list {
  margin-bottom: 30px;
}

.add-api-form {
  border-top: 1px solid #ebeef5;
  padding-top: 20px;
}

:deep(.el-dialog__body) {
  padding: 0;
}

:deep(.el-dialog__header) {
  padding: 15px 20px;
  margin-right: 0;
  border-bottom: 1px solid #e8e8e8;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.list-header h3 {
  margin: 0;
  color: #606266;
  font-size: 16px;
  font-weight: 500;
}
</style> 